import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { BlogPost as BlogPostType } from '@/types/blog';
import { getBlogPostBySlug } from '@/utils/contentLoader';
import BlogPost from '@/components/blog/BlogPost';
import BlogSEO from '@/components/blog/BlogSEO';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        const blogPost = await getBlogPostBySlug(slug);
        if (blogPost) {
          setPost(blogPost);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error loading blog post:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen font-sans bg-background">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded w-32 mb-8"></div>
              <div className="h-12 bg-muted rounded w-3/4 mb-4"></div>
              <div className="h-6 bg-muted rounded w-full mb-2"></div>
              <div className="h-6 bg-muted rounded w-2/3 mb-8"></div>
              <div className="h-64 bg-muted rounded mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !post) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className="flex flex-col min-h-screen font-sans bg-background dark">
      <BlogSEO post={post} />
      <Header />
      
      <main className="flex-1">
        <BlogPost post={post} />
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPostPage;
